/**
 * Context for managing dependency visualization state
 * Handles position tracking, drag state, and interactions
 */

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  BubbleType,
  DragState,
  ItemBounds,
  ItemDependency,
  OnDependencyCreateCallback,
  OnDependencyDeleteCallback,
  Point,
} from './types';
import { DependencyType } from './types';
import { useCentralizedPositionManager } from './useCentralizedPositionManager';

interface DependencyContextValue {
  itemPositions: Map<number, ItemBounds>;
  dragState: DragState;
  hoveredItemId: number | null;
  dependencies: Map<number, ItemDependency[]>;
  isResizing: boolean;
  setIsResizing: (resizing: boolean) => void;
  registerItemPosition: (id: number, bounds: ItemBounds) => void;
  unregisterItemPosition: (id: number) => void;
  registerElement: (id: number, element: HTMLElement) => void;
  unregisterElement: (id: number) => void;
  trackItemForRendering: (id: number) => void;
  untrackItemForRendering: (id: number) => void;
  setHoveredItem: (id: number | null) => void;
  startDependencyDrag: (itemId: number, type: BubbleType, position: Point) => void;
  updateDragPosition: (position: Point) => void;
  setDragTarget: (target: { itemId: number; type: BubbleType } | null) => void;
  endDependencyDrag: () => void;
  isValidDropTarget: (targetId: number, targetType: BubbleType) => boolean;
  onDependencyCreate?: OnDependencyCreateCallback;
  onDependencyDelete?: OnDependencyDeleteCallback;
  timelineContainerRef: React.RefObject<HTMLDivElement | null> | null;
  svgRef: React.RefObject<SVGSVGElement | null> | null;
  setTimelineContainerRef: (ref: HTMLDivElement | null) => void;
  setSvgRef: (ref: SVGSVGElement | null) => void;
  isContainerReady: boolean;
  includeDependencies: boolean;
  isCompact: boolean;
}

const DependencyContext = createContext<DependencyContextValue | null>(null);

interface DependencyProviderProps {
  children: ReactNode;
  items: Array<{
    id: number;
    dependencies?: ItemDependency[];
  }>;
  onDependencyCreate?: OnDependencyCreateCallback;
  onDependencyDelete?: OnDependencyDeleteCallback;
  includeDependencies: boolean;
  isCompact?: boolean;
}

export function DependencyProvider({
  children,
  items,
  onDependencyCreate,
  onDependencyDelete,
  includeDependencies,
  isCompact = false,
}: DependencyProviderProps) {
  const positionsRef = useRef<Map<number, ItemBounds>>(new Map());
  const [itemPositions, setItemPositions] = useState<Map<number, ItemBounds>>(new Map());
  const trackedItemsRef = useRef<Set<number>>(new Set());

  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    source: null,
    position: null,
    target: null,
  });
  const [isResizing, setIsResizing] = useState(false);

  const [hoveredItemId, setHoveredItemId] = useState<number | null>(null);

  const timelineContainerRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);

  const [isContainerReady, setIsContainerReady] = useState(false);

  const dependencies = useMemo(() => {
    const deps = new Map<number, ItemDependency[]>();
    for (const item of items) {
      if (item.dependencies && item.dependencies.length > 0) {
        deps.set(item.id, item.dependencies);
      }
    }
    return deps;
  }, [items]);

  const handlePositionUpdate = useCallback((id: number, bounds: ItemBounds) => {
    positionsRef.current.set(id, bounds);

    if (trackedItemsRef.current.has(id)) {
      setItemPositions((prev) => {
        const next = new Map(prev);
        next.set(id, bounds);
        return next;
      });
    }
  }, []);

  const { registerElement, unregisterElement } = useCentralizedPositionManager(
    timelineContainerRef,
    positionsRef,
    handlePositionUpdate,
  );

  const trackItemForRendering = useCallback((id: number) => {
    trackedItemsRef.current.add(id);
    const pos = positionsRef.current.get(id);
    if (pos) {
      setItemPositions((prev) => new Map(prev).set(id, pos));
    }
  }, []);

  const untrackItemForRendering = useCallback((id: number) => {
    trackedItemsRef.current.delete(id);
    setItemPositions((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const registerItemPosition = useCallback(
    (id: number, bounds: ItemBounds) => {
      handlePositionUpdate(id, bounds);
    },
    [handlePositionUpdate],
  );

  const unregisterItemPosition = useCallback((id: number) => {
    positionsRef.current.delete(id);
    trackedItemsRef.current.delete(id);
    setItemPositions((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  // Drag interaction
  const startDependencyDrag = useCallback((itemId: number, type: BubbleType, position: Point) => {
    setDragState({
      isDragging: true,
      source: { itemId, type, position },
      position,
      target: null,
    });
  }, []);

  const updateDragPosition = useCallback((position: Point) => {
    setDragState((prev) => ({
      ...prev,
      position,
    }));
  }, []);

  const setDragTarget = useCallback((target: { itemId: number; type: BubbleType } | null) => {
    setDragState((prev) => ({
      ...prev,
      target,
    }));
  }, []);

  // Validation - defined before endDependencyDrag to avoid circular dependency
  const isValidDropTarget = useCallback(
    (targetId: number, targetType: BubbleType): boolean => {
      const { source } = dragState;
      if (!source) return false;

      // Can't link to self
      if (source.itemId === targetId) return false;

      // Must be opposite bubble types
      if (source.type === 'right' && targetType !== 'left') return false;
      if (source.type === 'left' && targetType !== 'right') return false;

      // Check for existing dependency
      const itemDeps = dependencies.get(source.itemId) || [];
      const hasExisting = itemDeps.some((dep) => dep.dependencyItemId === targetId);
      if (hasExisting) return false;

      return true;
    },
    [dragState, dependencies],
  );

  const endDependencyDrag = useCallback(() => {
    const { source, target, position } = dragState;

    let finalTarget = target;

    // If no explicit target but we have a position, check if we're dropping over an item
    if (source && !target && position && onDependencyCreate) {
      // Find item under cursor
      for (const [itemId, bounds] of itemPositions.entries()) {
        if (itemId === source.itemId) continue;

        const isOverItem =
          position.x >= bounds.x &&
          position.x <= bounds.x + bounds.width &&
          position.y >= bounds.y &&
          position.y <= bounds.y + bounds.height;

        if (isOverItem) {
          // Determine target bubble type based on source type
          // If dragging from right, target the left bubble (and vice versa)
          const targetType = source.type === 'right' ? 'left' : 'right';

          // Validate this would be a valid drop
          if (isValidDropTarget(itemId, targetType)) {
            finalTarget = { itemId, type: targetType };
          }
          break;
        }
      }
    }

    // If we have a valid source and target, create the dependency
    if (source && finalTarget && onDependencyCreate) {
      const dependencyType = determineDependencyType(source.type, finalTarget.type);
      if (dependencyType) {
        onDependencyCreate({
          sourceItemId: source.itemId,
          dependencyType,
          dependencyItemId: finalTarget.itemId,
        });
      }
    }

    // Reset drag state
    setDragState({
      isDragging: false,
      source: null,
      position: null,
      target: null,
    });
  }, [dragState, onDependencyCreate, itemPositions, isValidDropTarget]);

  const setTimelineContainerRef = useCallback((ref: HTMLDivElement | null) => {
    timelineContainerRef.current = ref;
    setIsContainerReady(ref !== null);
  }, []);

  const setSvgRef = useCallback((ref: SVGSVGElement | null) => {
    svgRef.current = ref;
  }, []);

  const value = useMemo<DependencyContextValue>(
    () => ({
      itemPositions,
      dragState,
      hoveredItemId,
      dependencies,
      isResizing,
      setIsResizing,
      registerItemPosition,
      unregisterItemPosition,
      registerElement,
      unregisterElement,
      trackItemForRendering,
      untrackItemForRendering,
      setHoveredItem: setHoveredItemId,
      startDependencyDrag,
      updateDragPosition,
      setDragTarget,
      endDependencyDrag,
      isValidDropTarget,
      onDependencyCreate,
      onDependencyDelete,
      timelineContainerRef,
      svgRef,
      setTimelineContainerRef,
      setSvgRef,
      isContainerReady,
      includeDependencies,
      isCompact,
    }),
    [
      itemPositions,
      dragState,
      hoveredItemId,
      dependencies,
      isResizing,
      registerItemPosition,
      unregisterItemPosition,
      registerElement,
      unregisterElement,
      trackItemForRendering,
      untrackItemForRendering,
      startDependencyDrag,
      updateDragPosition,
      setDragTarget,
      endDependencyDrag,
      isValidDropTarget,
      onDependencyCreate,
      onDependencyDelete,
      setTimelineContainerRef,
      setSvgRef,
      isContainerReady,
      includeDependencies,
      isCompact,
    ],
  );

  return <DependencyContext.Provider value={value}>{children}</DependencyContext.Provider>;
}

/**
 * Hook to access dependency context
 */
export function useDependencyContext() {
  const context = useContext(DependencyContext);
  if (!context) {
    throw new Error('useDependencyContext must be used within DependencyProvider');
  }
  return context;
}

/**
 * Determine dependency type based on bubble connection
 */
function determineDependencyType(
  sourceBubble: BubbleType,
  targetBubble: BubbleType,
): DependencyType | null {
  if (sourceBubble === 'right' && targetBubble === 'left') {
    return DependencyType.Successor;
  }

  if (sourceBubble === 'left' && targetBubble === 'right') {
    return DependencyType.Predecessor;
  }

  return null;
}
